import { useFormikContext } from 'formik';
import React, { useEffect } from 'react';

import InfoIcon from '@mui/icons-material/Info';
import { Grid, Tooltip, InputAdornment, Typography } from '@mui/material';

import { Job } from '@libs/graphql-types';
import { modelPath } from '@libs/helpers/form';
import { ConnectedTextField } from '@libs/ui/components/form/text-field';

import { generateWtwSecurityId } from '../../security-id-utils';
import { ApplicationFormState } from '../../types';
import { BirthDateFields } from './BirthDateFields';

interface WtwSocialSecurityIdProps {
  job: Job;
  showOnlyRequired?: boolean;
}

/**
 * Hook to automatically sync the WTW security ID field when separate components change
 */
export const useWtwSecurityIdSync = () => {
  const { values, setFieldValue } = useFormikContext<ApplicationFormState>();

  useEffect(() => {
    const { firstName, lastName } = values.basicInfo;
    const { monthOfBirth, dayOfBirth, ssnOrPassportLast3Digits } =
      values.details;

    // Only generate if all required fields are present
    if (
      firstName &&
      lastName &&
      monthOfBirth > 0 &&
      dayOfBirth > 0 &&
      ssnOrPassportLast3Digits &&
      ssnOrPassportLast3Digits.length === 3
    ) {
      const securityId = generateWtwSecurityId(
        firstName,
        lastName,
        monthOfBirth,
        dayOfBirth,
        ssnOrPassportLast3Digits,
      );

      // Update both security ID and confirm security ID fields
      if (securityId !== values.details.securityId) {
        setFieldValue('details.securityId', securityId);
        setFieldValue('details.confirmSecurityId', securityId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.basicInfo.firstName,
    values.basicInfo.lastName,
    values.details.monthOfBirth,
    values.details.dayOfBirth,
    values.details.ssnOrPassportLast3Digits,
    values.details.securityId,
    setFieldValue,
  ]);
};

/**
 * WTW-specific Social Security ID form component
 * This component renders a custom form for WTW job applications
 * Format: DDMMFLXXX (DD + MM + F initial + L initial + last 3 digits of SSN/Passport)
 */
export const WtwSocialSecurityId: React.FC<WtwSocialSecurityIdProps> = ({
  job,
  showOnlyRequired = false,
}) => {
  useWtwSecurityIdSync();

  return (
    <>
      <Grid item>
        <Typography variant="h6" gutterBottom>
          Security ID Information{' '}
          <Typography component="span" color="error">
            *
          </Typography>
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Security ID format: DDMMFLXXX (Day + Month + First initial + Last
          initial + Last 3 digits of SSN or Passport). Example: John Doe, born
          05 March, SSN/Passport ending in 789 → 0503JD789
        </Typography>
      </Grid>

      {/* Birth date fields - reused component */}
      <Grid item>
        <BirthDateFields showOnlyRequired={showOnlyRequired} />
      </Grid>

      <Grid item>
        <ConnectedTextField
          name={modelPath<ApplicationFormState>(
            (i) => i.details.ssnOrPassportLast3Digits,
          )}
          fullWidth
          variant="filled"
          label="Last 3 digits of SSN or Passport Number"
          placeholder="789"
          inputProps={{ maxLength: 3 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip
                  title={
                    <div>
                      <strong>
                        Last 3 digits of Social Security Number or Passport
                        Number
                      </strong>
                      <br />
                      <br />
                      Enter the last 3 digits of your SSN or Passport Number.
                      This is used to generate your unique Security ID for WTW.
                    </div>
                  }
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid item>
        <ConnectedTextField
          name={modelPath<ApplicationFormState>((i) => i.details.securityId)}
          fullWidth
          variant="filled"
          label="Security ID"
          disabled
          placeholder="DDMMFLXXX"
          inputProps={{ maxLength: 9 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip
                  title={
                    <div>
                      <strong>Security ID Format: DDMMFLXXX</strong>
                      <br />
                      <br />
                      <strong>DD</strong> – Two-digit Birth Day
                      <br />
                      <strong>MM</strong> – Two-digit Birth Month
                      <br />
                      <strong>F</strong> – Initial of First Name
                      <br />
                      <strong>L</strong> – Initial of Last Name
                      <br />
                      <strong>XXX</strong> – Last three digits of candidate's
                      Social Security Number or Passport Number
                      <br />
                      <br />
                      Example: John Doe, born 05 March, SSN: 123456789 →
                      Security ID: 0503JD789
                    </div>
                  }
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
    </>
  );
};
